// src/ShareQuiz.js
import React from "react";
import { useLocation } from "react-router-dom";
import { auth, db, storage } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { ref, getMetadata } from "firebase/storage";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import Menu from "./Menu";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function ShareQuiz() {
  const qs = useQuery();
  const fileName = qs.get("file");

  const [user, setUser] = React.useState(null);
  const [claims, setClaims] = React.useState({});
  const [ownerUid, setOwnerUid] = React.useState(null);
  const [shares, setShares] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState("");
  const [emailToAdd, setEmailToAdd] = React.useState("");

  const [bgUrl, setBgUrl] = React.useState("");
  const [showMenu, setShowMenu] = React.useState(false);

  // Background image (optional)
  React.useEffect(() => {
    (async () => {
      try {
        const resp = await fetch("/media/Brainbackground.png");
        if (resp.ok) setBgUrl("/media/Brainbackground.png");
      } catch {
        // ignore
      }
    })();
  }, []);

  // Auth / claims
  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const tok = await u.getIdTokenResult();
        setClaims(tok.claims || {});
      } else {
        setClaims({});
      }
    });
    return () => unsub();
  }, []);

  // Resolve owner from Storage metadata
  React.useEffect(() => {
    if (!fileName) return;
    (async () => {
      try {
        const fileRef = ref(storage, `quizzes/${fileName}`);
        const meta = await getMetadata(fileRef);
        setOwnerUid(meta?.customMetadata?.ownerUid || null);
      } catch {
        setOwnerUid(null);
      }
    })();
  }, [fileName]);

  const canManage =
    !!user && (user.uid === ownerUid || claims?.role === "Qwizard");

  // LIVE list of shares for this quiz
  React.useEffect(() => {
    if (!fileName) return;
    const col = collection(db, "quizzes", fileName, "shares");
    const unsub = onSnapshot(
      col,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setShares(rows);
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );
    return () => unsub();
  }, [fileName]);

  // Add share by email (no invitation)
  const addShare = async () => {
    setStatus("");
    const email = (emailToAdd || "").trim().toLowerCase();
    if (!email || !fileName) return;

    try {
      // 1) find user by email in your "users" collection
      const usersCol = collection(db, "users");
      const qy = query(usersCol, where("email", "==", email));
      const snap = await getDocs(qy);
      if (snap.empty) {
        setStatus(
          "No user with that email. Ask them to sign in once, then try again."
        );
        return;
      }
      const userDoc = snap.docs[0];
      const target = { uid: userDoc.id, ...(userDoc.data() || {}) };

      // 2) update the quiz doc's sharedWithUids
      const quizRef = doc(db, "quizzes", fileName);
      await updateDoc(quizRef, { sharedWithUids: arrayUnion(target.uid) });

      // 3) mirror in subcollection for easy listing
      await setDoc(
        doc(db, "quizzes", fileName, "shares", target.uid),
        {
          uid: target.uid,
          email: target.email || email,
          displayName: target.displayName || null,
          sharedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setEmailToAdd("");
      setStatus(
        `User found. Access granted to ${target.displayName || email}.`
      );
    } catch (e) {
      setStatus(e?.message || "Failed to add share.");
    }
  };

  // Revoke share (no confirm dialog, per requirements)
  const removeShare = async (uid) => {
    if (!uid || !fileName) return;
    setStatus("");
    try {
      const quizRef = doc(db, "quizzes", fileName);
      await updateDoc(quizRef, { sharedWithUids: arrayRemove(uid) });
      await deleteDoc(doc(db, "quizzes", fileName, "shares", uid));
      setStatus("Access revoked.");
    } catch (e) {
      setStatus(e?.message || "Failed to revoke access.");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        background: bgUrl
          ? `url(${bgUrl}) center center / cover no-repeat`
          : "#223b4c",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        fontFamily: "inherit",
      }}
    >
      {/* top bar */}
      <div
        style={{
          width: "100vw",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>
        <div style={{ marginLeft: 8, fontWeight: 900, color: "#f8faff" }}>
          Quizmaster
        </div>
      </div>

      <Menu open={showMenu} onClose={() => setShowMenu(false)} onReconnect={() => {}} />

      <div style={{ width: "100%", maxWidth: 720, margin: "24px auto" }}>
        <div
          style={{
            background: "rgba(28,34,58,0.13)",
            borderRadius: "1.5em",
            padding: "18px 20px",
            boxShadow: "0 8px 24px 0 #0003",
            color: "#f6fcff",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>
            Share settings for:{" "}
            <span style={{ opacity: 0.9 }}>{fileName || "(unknown)"}</span>
          </h2>

          {!user ? (
            <div style={{ marginTop: 16 }}>
              Please sign in to manage sharing.
            </div>
          ) : !canManage ? (
            <div style={{ marginTop: 16, fontSize: 18 }}>
              I'm sorry but you are not the owner of this Quiz and so you are unable to share it
            </div>
          ) : (
            <>
              {/* Add by email (no invitations sent) */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>
                  Add by email
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <input
                    type="email"
                    placeholder="someone@example.com"
                    value={emailToAdd}
                    onChange={(e) => setEmailToAdd(e.target.value)}
                    style={{
                      flex: 1,
                      padding: 12,
                      fontSize: 16,
                      borderRadius: 10,
                      border: "1px solid #8891aa",
                    }}
                  />
                  <button
                    onClick={addShare}
                    style={{
                      background: "#ff9800",
                      color: "#222",
                      fontWeight: 800,
                      border: "none",
                      borderRadius: 10,
                      padding: "12px 18px",
                    }}
                    disabled={!emailToAdd.trim()}
                  >
                    Add
                  </button>
                </div>
                <div style={{ marginTop: 6, fontSize: 12, color: "#b5b8c6" }}>
                  This grants access immediately. No email is sent.
                </div>
              </div>

              {/* Share list */}
              <div
                style={{
                  marginTop: 18,
                  background: "rgba(35,35,61,0.23)",
                  borderRadius: 14,
                  padding: "12px 12px",
                }}
              >
                {loading ? (
                  <div>Loading…</div>
                ) : shares.length === 0 ? (
                  <div style={{ color: "#b5b8c6" }}>No shares yet.</div>
                ) : (
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {shares.map((row) => (
                      <li
                        key={row.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 6px",
                          borderBottom:
                            "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700 }}>
                            {row.displayName
                              ? `${row.displayName} (${
                                  row.email || row.uid
                                })`
                              : row.email || row.uid}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#b5b8c6",
                              marginTop: 2,
                            }}
                          >
                            Shared:{" "}
                            {row.sharedAt?.toDate
                              ? row.sharedAt.toDate().toLocaleString()
                              : row.sharedAt
                              ? new Date(row.sharedAt).toLocaleString()
                              : "n/a"}
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            removeShare(row.uid || row.id)
                          }
                          style={{
                            background: "#ff4a4a",
                            color: "#fff",
                            fontWeight: 700,
                            border: "none",
                            borderRadius: 10,
                            padding: "8px 14px",
                            cursor: "pointer",
                          }}
                        >
                          Revoke
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {!!status && (
                <div style={{ marginTop: 10, fontSize: 14 }}>{status}</div>
              )}

              <div style={{ marginTop: 14, fontSize: 13, color: "#b5b8c6" }}>
                Only the owner or a Qwizard can change sharing.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
